import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getKnowledgeCoverage, draftSop } from "../../../api/sopsApi";
import { describeApiError } from "../../../api/client";
import "../OpsShared.css";
import "../../Team/people.css";
import "./sops.css";
import QuestionQueue from "../Documents/QuestionQueue";

/* Operations → Knowledge: what the assistant can actually answer about this
   business, and where the holes are (crm_backend docs/KNOWLEDGE_COVERAGE_CONTRACT.md).
   Three states per question — documented (a published SOP or a confirmed field),
   inferred (a candidate nobody confirmed yet), missing — and every missing one
   offers "Draft an SOP for this", which runs the AI SOP maker and drops the
   manager into the review conversation on the new draft. */

const STATUS = {
  documented: { label: "Documented", cls: "approved" },
  inferred: { label: "Waiting on you", cls: "pending" },
  missing: { label: "Missing", cls: "" },
};

export default function KnowledgePage() {
  const navigate = useNavigate();
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drafting, setDrafting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await getKnowledgeCoverage(); setRep(r.data); setError(null); }
    catch (e) { setError(describeApiError(e, { fallback: "Couldn’t read your knowledge coverage." })); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const draft = async (gap) => {
    setDrafting(gap.draft_title);
    try {
      const r = await draftSop({ title: gap.draft_title, category: gap.category, notes: `Answers the question: ${gap.question}` });
      toast.success(`Drafted “${r.data.title}” — ${r.data.grounded_steps} of ${r.data.total_steps} steps from your own records`);
      navigate(`/operations/sops?open=${encodeURIComponent(r.data.slug)}`);
    } catch (ex) {
      toast.error(describeApiError(ex, { forbidden: "Only an admin or manager can draft SOPs.",
        fallback: "The drafter didn’t answer — nothing was written." }));
    } finally { setDrafting(null); }
  };

  if (loading) return <section className="ppl-card"><div className="ppl-empty"><strong>Reading what the assistant knows…</strong></div></section>;
  if (error) return <section className="ppl-card"><div className="ppl-empty"><strong>{error}</strong><button className="ops-btn" onClick={load}>Try again</button></div></section>;
  if (!rep) return null;

  const t = rep.totals;
  return (
    <div className="kn-page">
      <QuestionQueue compact openOnly />
      <section className="ppl-card kn-hero">
        <div className="kn-hero-main">
          <h2>{rep.headline}</h2>
          <p className="ppl-sub">
            Documented means a published SOP or a confirmed setting — that’s what it can say today.
            Waiting on you means we drafted or inferred something nobody has approved yet.
            {rep.library ? ` Your library: ${rep.library.published} published, ${rep.library.drafts} draft${rep.library.drafts === 1 ? "" : "s"}.` : ""}
          </p>
          <div className="kn-bar" role="img" aria-label={`${t.percent}% documented`}>
            <span className="kn-bar-doc" style={{ width: `${(100 * t.documented) / Math.max(1, t.questions)}%` }} />
            <span className="kn-bar-inf" style={{ width: `${(100 * t.inferred) / Math.max(1, t.questions)}%` }} />
          </div>
          <div className="kn-legend">
            <span><i className="kn-dot kn-dot--doc" />{t.documented} documented</span>
            <span><i className="kn-dot kn-dot--inf" />{t.inferred} waiting on you</span>
            <span><i className="kn-dot kn-dot--miss" />{t.missing} missing</span>
          </div>
        </div>
        <div className="kn-score"><strong>{t.percent}%</strong><span>answerable</span></div>
      </section>

      {rep.pending_setup?.length > 0 && (
        <section className="ppl-card kn-pending">
          <strong>{rep.pending_setup.length} thing{rep.pending_setup.length === 1 ? "" : "s"} are proposed and waiting for you to confirm</strong>
          <span className="ppl-sub">Confirming turns them into answers the assistant may give.</span>
          <button className="ops-btn" onClick={() => navigate("/settings?tool=setup")}>Open Setup</button>
        </section>
      )}

      <div className="kn-areas">
        {rep.areas.map((a) => (
          <section key={a.key} className="ppl-card kn-area">
            <div className="kn-area-head">
              <h3>{a.label}</h3>
              <span className={`ppl-pill ${a.percent === 100 ? "approved" : a.documented ? "pending" : ""}`}>{a.documented}/{a.total}</span>
            </div>
            <ul className="kn-qs">
              {a.questions.map((q) => (
                <li key={q.key} className={`kn-q is-${q.status}`}>
                  <div className="kn-q-main">
                    <span className="kn-q-text">{q.question}</span>
                    <span className="kn-q-evidence">{q.evidence}</span>
                  </div>
                  <div className="kn-q-side">
                    <span className={`ppl-pill ${STATUS[q.status].cls}`}>{STATUS[q.status].label}</span>
                    {q.slug && <button className="ops-btn secondary" onClick={() => navigate(`/operations/sops?open=${encodeURIComponent(q.slug)}`)}>Open</button>}
                    {q.status === "missing" && (
                      <button className="ops-btn" disabled={drafting === q.draft_title} onClick={() => draft({ ...q, category: (rep.gaps.find((g) => g.draft_title === q.draft_title) || {}).category || "general" })}>
                        {drafting === q.draft_title ? "Drafting…" : "Draft an SOP for this"}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
